// La finestra «Richiama dopo» (piano M5, D13; decisione del product owner del 25 settembre 2026): i richiami si fissano
// dal lunedì al venerdì dalle 08:30 alle 20:00, venerdì compreso e le 20:00 comprese. La finestra offre 16 blocchi «Tra
// qualche ora» da +30 minuti a +8 ore di lavoro con l'orario già calcolato (oltre le 20:00 si passa alla mattina dopo), i
// due giorni lavorativi successivi con un orario ogni mezz'ora, e «Altro giorno e ora» a quarti d'ora. Funzioni pure,
// come `mockup.js` (`addWorkingMinutes`, `nextWorkingDay`, `checkCustomCallback`): ricevono l'istante di adesso e il
// calendario dell'azienda e restituiscono istanti assoluti; le etichette sono dei cataloghi.
//
// Il calendario dei richiami non è quello dell'azienda: ha le sue ore (`DEFAULT_CALLBACK_HOURS`), mentre il fuso e le
// chiusure sono quelli dell'azienda. Il parametro del calendario è quindi `CallbackCalendar`, soltanto `timeZone` e
// `closedDates`: il tipo dice ciò che si legge, e gli orari della settimana dell'azienda (08:30–18:00 per le chiamate della
// sequenza) non stringono la finestra dei richiami. Le ore dei richiami restano fisse finché le impostazioni «Richiami»
// (questione aperta 260) non aggiungono un parametro esplicito per le ore. Il calcolo è quello di `addWorkingHours` di
// `callback-scheduling`, le giornate e le ore di apertura quelle di `workingWindowOf`.
import { addWorkingHours, instantFromLocal, searchLimitDays, workingWindowOf, zonedPartsOf, } from "./callback-scheduling.js";
import { addLocalDays, dayString, localDateOf, requireInstant, requireTimeZone } from "./local-days.js";
/** Le ore dei richiami: dal lunedì al venerdì 08:30–20:00, le 20:00 comprese; sabato e domenica chiusi (decisione del 25 settembre 2026). */
export const DEFAULT_CALLBACK_HOURS = Object.freeze({
    1: Object.freeze({ start: "08:30", end: "20:00", endInclusive: true }),
    2: Object.freeze({ start: "08:30", end: "20:00", endInclusive: true }),
    3: Object.freeze({ start: "08:30", end: "20:00", endInclusive: true }),
    4: Object.freeze({ start: "08:30", end: "20:00", endInclusive: true }),
    5: Object.freeze({ start: "08:30", end: "20:00", endInclusive: true }),
    6: null,
    7: null,
});
/** I 16 blocchi «Tra qualche ora»: il blocco `n` (da 1) sono `n` mezz'ore di lavoro dei richiami da adesso. */
export const CALLBACK_SOON_PRESETS = ["+30m", "+1h", "+1h30", "+2h", "+2h30", "+3h", "+3h30", "+4h", "+4h30", "+5h", "+5h30", "+6h", "+6h30", "+7h", "+7h30", "+8h"];
/**
 * Quanto lontano si può fissare un richiamo: 90 giorni di calendario dal giorno di adesso, nel fuso dell'azienda. È una
 * proposta di Claude, questione aperta 304 (il mock-up non ha un limite e la specifica non ne dice): impedisce un anno
 * sbagliato per una svista («2062») e lascia ancora tre mesi, quanto la finestra dell'annullamento di un'azione multipla.
 */
export const CALLBACK_HORIZON_DAYS = 90;
/** Perché un orario non è un richiamo valido, nell'ordine in cui `validateCallbackAt` li controlla. */
export const CALLBACK_INVALID_REASONS = ["past", "closed_day", "outside_hours", "not_quarter", "too_far"];
const MINUTE_MS = 60_000;
const QUARTER_MINUTES = 15;
const DAY_SLOT_MINUTES = 30;
const WORKING_DAYS_OFFERED = 2;
/** Il calendario dei richiami: le ore dei richiami nel fuso e con le chiusure dell'azienda. */
function callbackCalendarOf(calendar) {
    const timeZone = requireTimeZone(calendar?.timeZone);
    return { timeZone, weeklyHours: DEFAULT_CALLBACK_HOURS, closedDates: calendar.closedDates };
}
/** L'ultimo giorno in cui si può fissare un richiamo, visto da `now`: il giorno locale di adesso più l'orizzonte. */
function latestDayOf(now, timeZone) {
    return dayString(addLocalDays(localDateOf(now, timeZone), CALLBACK_HORIZON_DAYS));
}
/** Il giorno aperto che segue `after`, con le sue ore; si cerca per due settimane più le chiusure, come il resto del calendario. */
function nextOpenDay(after, calendar) {
    const limit = searchLimitDays(calendar.closedDates?.length);
    for (let offset = 1; offset <= limit; offset += 1) {
        const date = addLocalDays(after, offset);
        const window = workingWindowOf(date, calendar);
        if (window)
            return { date, window };
    }
    throw new RangeError("No working day was found for the callbacks");
}
/** Un orario ogni mezz'ora dall'apertura alla chiusura, la chiusura compresa quando lo è nel calendario. */
function halfHourSlots(window) {
    const step = DAY_SLOT_MINUTES * MINUTE_MS;
    const slots = [];
    for (let time = window.open.getTime(); time < window.close.getTime() || (time === window.close.getTime() && window.endInclusive); time += step) {
        slots.push(new Date(time));
    }
    return slots;
}
/** Il minuto locale dell'istante, per misurare quanto manca al quarto d'ora; il fuso può avere uno scarto non intero in ore. */
function minutesPastQuarter(instant, timeZone) {
    return zonedPartsOf(instant, timeZone).minute % QUARTER_MINUTES;
}
/**
 * I 16 blocchi in ore di lavoro dei richiami (oltre la chiusura si passa alla mattina dopo), i due giorni lavorativi
 * successivi a mezz'ore, i limiti di «Altro giorno e ora». Come nel mock-up l'orario di ogni blocco si arrotonda al quarto
 * d'ora successivo (alle 14:20, +30 minuti sono le 15:00 e non le 14:50), così ogni orario offerto è anche valido per
 * `validateCallbackAt`; i minuti sono quelli dell'orologio, secondi e millisecondi di `now` non contano. Chiusure e fuso
 * sono quelli di `calendar` (`CallbackCalendar`: nessun orario della settimana, e un calendario d'azienda con i suoi
 * `weeklyHours` non li cambia); le ore dei richiami sono `DEFAULT_CALLBACK_HOURS`, fisse finché le impostazioni
 * «Richiami» (questione aperta 260) non aggiungono un parametro esplicito per le ore.
 */
export function callbackSlots(now, calendar) {
    const callbackCalendar = callbackCalendarOf(calendar);
    const { timeZone } = callbackCalendar;
    const start = new Date(Math.floor(requireInstant(now, "now") / MINUTE_MS) * MINUTE_MS);
    // Il risultato di `addWorkingHours` cade in un minuto intero e dentro le ore dei richiami; la chiusura (20:00) è un
    // quarto d'ora, quindi arrotondare per eccesso non la oltrepassa mai.
    const soon = CALLBACK_SOON_PRESETS.map((preset, index) => {
        const worked = new Date(addWorkingHours(start.toISOString(), (index + 1) / 2, callbackCalendar).iso);
        const behind = minutesPastQuarter(worked, timeZone);
        return { preset, at: new Date(worked.getTime() + (behind === 0 ? 0 : QUARTER_MINUTES - behind) * MINUTE_MS) };
    });
    const days = [];
    let day = localDateOf(start, timeZone);
    for (let index = 0; index < WORKING_DAYS_OFFERED; index += 1) {
        const next = nextOpenDay(day, callbackCalendar);
        days.push({ day: dayString(next.date), slots: halfHourSlots(next.window) });
        day = next.date;
    }
    // Il primo quarto d'ora dopo adesso (adesso stesso è già passato) e, se cade fuori dalle ore dei richiami o in un giorno
    // chiuso, la prossima apertura: `addWorkingHours` con zero ore porta l'istante dentro la finestra senza spostarlo se c'è già.
    const quarter = new Date(start.getTime() + (QUARTER_MINUTES - minutesPastQuarter(start, timeZone)) * MINUTE_MS);
    const earliest = new Date(addWorkingHours(quarter.toISOString(), 0, callbackCalendar).iso);
    return { soon, days, custom: { stepMinutes: 15, earliest, latestDay: latestDayOf(start, timeZone) } };
}
/**
 * Se `at` è un richiamo che si può fissare adesso, e altrimenti il primo motivo (nell'ordine di `CALLBACK_INVALID_REASONS`):
 * non dopo `now` (adesso stesso è già passato, come nel mock-up), un giorno chiuso (sabato, domenica, una chiusura
 * dell'azienda), fuori dalle 08:30–20:00 (le 20:00 sono ancora buone), non a quarti d'ora (minuti, secondi o millisecondi),
 * oltre `CALLBACK_HORIZON_DAYS` giorni. Giorno e ore sono quelli del fuso dell'azienda; di `calendar` (`CallbackCalendar`)
 * contano soltanto `timeZone` e `closedDates`, come in `callbackSlots`: le ore dei richiami sono `DEFAULT_CALLBACK_HOURS`,
 * fisse finché le impostazioni «Richiami» (questione aperta 260) non aggiungono un parametro esplicito per le ore.
 */
export function validateCallbackAt(at, now, calendar) {
    const callbackCalendar = callbackCalendarOf(calendar);
    const { timeZone } = callbackCalendar;
    const time = requireInstant(at, "at");
    if (time <= requireInstant(now, "now"))
        return { ok: false, reason: "past" };
    const local = zonedPartsOf(at, timeZone);
    const date = { year: local.year, month: local.month, day: local.day };
    const window = workingWindowOf(date, callbackCalendar);
    if (!window)
        return { ok: false, reason: "closed_day" };
    if (time < window.open.getTime() || time > window.close.getTime() || (time === window.close.getTime() && !window.endInclusive)) {
        return { ok: false, reason: "outside_hours" };
    }
    if (local.minute % QUARTER_MINUTES !== 0 || local.second !== 0 || local.millisecond !== 0)
        return { ok: false, reason: "not_quarter" };
    // Le date `AAAA-MM-GG` si confrontano come testo.
    if (dayString(date) > latestDayOf(now, timeZone))
        return { ok: false, reason: "too_far" };
    return { ok: true };
}
const LOCAL_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_TIME = /^(\d{2}):(\d{2})$/;
/**
 * «Altro giorno e ora» (piano M5, Task 17): il giorno (`AAAA-MM-GG`) e l'ora (`HH:mm`) scritti nella finestra, letti nel fuso
 * dell'azienda, come istante da mandare al server. `null` se il giorno o l'ora non si leggono, oppure se quell'ora non esiste
 * perché il cambio dell'ora la salta. Se l'istante è un richiamo valido lo dice `validateCallbackAt`, sul server.
 */
export function callbackInstantOf(day, time, timeZone) {
    const zone = requireTimeZone(timeZone);
    // Un fuso che non esiste è un errore di chi chiama, come nelle altre funzioni: non diventa «ora che non esiste».
    zonedPartsOf(new Date(0), zone);
    const dayMatch = LOCAL_DAY.exec(day);
    const timeMatch = LOCAL_TIME.exec(time);
    if (!dayMatch || !timeMatch)
        return null;
    const [year, month, date] = [Number(dayMatch[1]), Number(dayMatch[2]), Number(dayMatch[3])];
    const [hour, minute] = [Number(timeMatch[1]), Number(timeMatch[2])];
    // Un giorno che il calendario non ha (il 30 febbraio) torna diverso dal calcolo in UTC.
    const check = new Date(Date.UTC(year, month - 1, date));
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== date)
        return null;
    if (hour > 23 || minute > 59)
        return null;
    try {
        return instantFromLocal({ year, month, day: date }, { hour, minute }, zone);
    }
    catch (error) {
        if (error instanceof RangeError)
            return null;
        throw error;
    }
}
//# sourceMappingURL=callbacks.js.map